// End Counter -> item counter sync: saving the LAST entry of a Captain's Log
// moves the item's own hour meter to that entry's End Counter, so logging the
// day's run is enough to keep counter-due tasks honest.
//
// Fired from each entry-edit surface's save callback, so the check runs after
// the entry's write has landed. The counter write itself rides the shared
// save queue on the counter's own lane rather than a legCascades-style chain:
// this is ONE idempotent, self-contained write to one record, and the queue
// already gives it everything the chain exists for -- it outlives the page
// (the commonest trigger is typing End Counter and navigating away, which
// flushes the entry save on unmount), serializes behind a hand edit of the
// same counter in EditHours (same `counter:` lane), coalesces two rapid syncs
// to the newest value, retries network errors, and shows in SaveStatusPill.
// legCascades is for multi-row read-relative cascades; this isn't one.
//
// The guards, in order:
//   - the type opts in (syncsItemCounter -- Captain's only today);
//   - the item has EXACTLY one counter. With several (engine hours and a
//     generator meter, say) nothing ties the log to one of them, and a wrong
//     counter write is worse than none;
//   - the entry carries a finite End Counter (0 is a value);
//   - the value differs from the counter's -- which also keeps every later
//     flush of the same entry (a notes keystroke, say) from spending the
//     last-entry read once the sync has landed;
//   - the entry is the logbook's STRICTLY latest (latestLogbookEntry): editing
//     an old entry must not roll the meter back, and two entries tied on one
//     day have no defined "last", so a tie syncs nothing.
//
// Deliberately out of scope: deleting the last entry does not roll the
// counter back, and logbook import never syncs (importing an old logbook must
// not clobber the meter -- import doesn't call this, which is the mechanism).

import * as Database from './Database.mjs';
import { saveQueue } from './SaveQueue.mjs';
import { counterLane } from './saveLanes.js';
import { carryForwardEndCounter, syncsItemCounter } from '../fleet-shared/LogbookTypes.mjs';

// Check the entry just saved and, when every guard passes, queue the counter
// write. `onCounterSaved(savedCounter)` lets the caller splice the new value
// into its in-memory item so HourMeter repaints without a refetch. Resolves
// once the DECISION is made -- the write itself is the queue's.
export async function syncItemCounterFromEntry({ entry, typeId, item, onCounterSaved }) {
  if (!entry?.id || !entry.logbookId) return;
  if (!syncsItemCounter(typeId)) return;

  const counters = item?.itemCounter?.items ?? [];
  if (counters.length !== 1) return;
  const counter = counters[0];

  // "This entry's finite End Counter, or null" -- the suggestion flow's read,
  // reused because it already handles the blob-arrives-as-a-string trap and
  // the genuine-0 case.
  const end = carryForwardEndCounter(entry, typeId);
  if (end === null) return;

  if (Number(counter.counter) === end) return;

  const latest = await Database.latestLogbookEntry(entry.logbookId);
  if (latest?.id !== entry.id) return;

  saveQueue.schedule(
    counterLane(counter.id),
    {
      run: async () => {
        // `name` is mandatory -- updateCounter throws without one -- and with
        // no `model` argument it returns null, so the lane's saved value is
        // the merged copy built here.
        await Database.updateCounter({ id: counter.id, name: counter.name, counter: end });
        return { ...counter, counter: end };
      },
      onSaved: (saved) => onCounterSaved?.(saved),
    },
    { delayMs: 0 }
  );
}

// A copy of `item` with the saved counter spliced into its counters -- what
// every caller's onCounterSaved does, so the splice is written once. Returns
// `item` untouched when it carries no counter list.
export function withUpdatedCounter(item, saved) {
  const items = item?.itemCounter?.items;
  if (!items) return item;
  return {
    ...item,
    itemCounter: {
      ...item.itemCounter,
      items: items.map((c) => (c.id === saved.id ? saved : c)),
    },
  };
}

export default syncItemCounterFromEntry;
